import React from 'react';
import {
  Box,
  Flex,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Info, Database, FunnelSimple } from 'phosphor-react';
import { UI } from '../ui';

interface InfoPopoverProps {
  /** Título curto exibido no header do popover. */
  title?: string;
  /** Conteúdo principal — texto simples ou JSX. */
  children: React.ReactNode;
  size?: number;
}

/**
 * Tooltip-toggle: ícone "i" que ao CLICAR abre/fecha um popover com a explicação
 * do card — o que é, dados cruzados e filtros em uso. Mesmo padrão da tela de
 * Fechamentos. `stopPropagation` evita disparar o onClick do card que o contém.
 */
export const InfoPopover = ({ title, children, size = 13 }: InfoPopoverProps) => (
  <Box as="span" onClick={e => e.stopPropagation()} display="inline-flex">
    <Popover trigger="click" placement="top" closeOnBlur>
      <PopoverTrigger>
        <IconButton
          aria-label="Mais informações sobre este card"
          icon={<Info size={size} weight="duotone" />}
          size="xs"
          variant="ghost"
          color={UI.faint}
          _hover={{ color: UI.brand, bg: UI.infoSoft }}
          h="auto"
          minW="auto"
          p={0.5}
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent fontSize="sm" maxW="340px" boxShadow="xl" zIndex={1500}>
          <PopoverArrow />
          <PopoverCloseButton />
          {title && (
            <PopoverHeader fontWeight={800} fontSize="13px" color={UI.ink}>
              {title}
            </PopoverHeader>
          )}
          <PopoverBody>
            {typeof children === 'string' ? (
              <Text fontSize="12px" color={UI.muted} lineHeight="1.55">
                {children}
              </Text>
            ) : (
              <Box fontSize="12px" color={UI.muted}>
                {children}
              </Box>
            )}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  </Box>
);

/** Frases de filtro reutilizáveis (o "qual filtro está usando"). */
export const FILTRO = {
  carteira:
    'Toda a carteira · período (data início–fim) selecionado no topo · respeita o botão “Só clientes ativos”.',
  empresa: 'Empresa selecionada no topo · período (data início–fim) escolhido na barra.',
  empresaSemPeriodo: 'Empresa selecionada no topo · sem recorte de período (estado atual).',
  historico: 'Sem período — valor acumulado/histórico da empresa, não varia com a data.',
} as const;

interface InfoBlocoProps {
  /** O que o card representa (1-2 linhas). */
  oque: React.ReactNode;
  /** Tabelas/campos cruzados. */
  dados: React.ReactNode;
  /** Filtros em uso (período, empresa, escopo...). */
  filtro: React.ReactNode;
}

/** Corpo padronizado do InfoPopover: O que é · Dados cruzados · Filtro. */
export const InfoBloco = ({ oque, dados, filtro }: InfoBlocoProps) => (
  <Stack spacing={3}>
    <Text fontSize="12px" color={UI.muted} lineHeight="1.55">
      {oque}
    </Text>
    <Stack spacing={1} pt={2} borderTop="1px solid" borderColor="rgba(15,23,42,0.08)">
      <Flex align="center" gap={1.5}>
        <Box color={UI.brand}>
          <Database size={13} weight="fill" />
        </Box>
        <Text
          fontSize="10px"
          fontWeight={800}
          color={UI.faint}
          textTransform="uppercase"
          letterSpacing="0.06em"
        >
          Dados cruzados
        </Text>
      </Flex>
      <Text fontSize="11.5px" color={UI.ink} pl={5}>
        {dados}
      </Text>
    </Stack>
    <Stack spacing={1}>
      <Flex align="center" gap={1.5}>
        <Box color={UI.warn}>
          <FunnelSimple size={13} weight="fill" />
        </Box>
        <Text
          fontSize="10px"
          fontWeight={800}
          color={UI.faint}
          textTransform="uppercase"
          letterSpacing="0.06em"
        >
          Filtro em uso
        </Text>
      </Flex>
      <Text fontSize="11.5px" color={UI.ink} pl={5}>
        {filtro}
      </Text>
    </Stack>
  </Stack>
);
